import { z } from 'zod'
import { captureAndReturn } from '@omnea-app/packages/universal/zod-utils/captureAndReturn'
import {
  createSupplierPreferredConditionOptions,
  createSupplierStateConditionOptions,
  SUPPLIER_PREFERRED_INVALID_OPERATORS,
} from './forms/customConditionOptionsCreators'
import { mapComparisonLeaves } from './mapComparisonLeaves'
import {
  comparisonSchemasFor,
  Comparison,
  ComparisonTree,
  ConditionOperator,
  LeftOperand,
  RequestLeftOperandSchemas,
  SingleComparison,
} from './schema'
import { createPrimitiveConditionVariable, createStringConditionVariable } from './validation/conditionVariableUtils'
import type { PrimitiveConditionVariable, StringConditionVariable } from './validation/types'

/**
 * A condition evaluated against an RFx supplier rather than a request — the sub-workflow trigger
 * rule, which asks whether a sub-workflow should start for one supplier of an RFx.
 *
 * It reads everything a condition on the RFx block reads, plus operands of its own: the RFx stage and
 * the organisation-supplier state of the RFx supplier. Operators, right operands and the AND/OR tree are
 * shared.
 */

export const RfxTriggerOperandSchema = z.object({
  type: z.literal('RFX_TRIGGER_VARIABLE'),
  value: z.enum([
    'rfx.launched',
    'rfx.responsesClosed',
    'rfx.scoringStarted',
    'rfx.scoringClosed',
    'rfxSupplier.state',
    'rfxSupplier.isPreferred',
  ]),
})
export type RfxTriggerOperand = z.infer<typeof RfxTriggerOperandSchema>

const RfxTriggerLeftOperandSchema = z
  .discriminatedUnion('type', [...RequestLeftOperandSchemas, RfxTriggerOperandSchema])
  .catch(captureAndReturn('RfxTriggerLeftOperandSchema'))
export type RfxTriggerLeftOperand = z.infer<typeof RfxTriggerLeftOperandSchema>

// Unannotated on purpose: the request tree is published as `Shared:Comparison`, and a left operand
// only this subject understands cannot appear in a shared client type.
const rfxTriggerComparison = comparisonSchemasFor(RfxTriggerLeftOperandSchema)

export type RfxTriggerSingleComparison = z.infer<typeof rfxTriggerComparison.single>

export const RfxTriggerComparisonSchema = rfxTriggerComparison.tree
export type RfxTriggerComparison = ComparisonTree<RfxTriggerSingleComparison>

export const isRfxTriggerOperand = (operand: RfxTriggerLeftOperand | null): operand is RfxTriggerOperand =>
  operand?.type === 'RFX_TRIGGER_VARIABLE'

export type RfxStageFlag = Extract<RfxTriggerOperand['value'], `rfx.${string}`>
export type RfxSupplierField = Extract<RfxTriggerOperand['value'], `rfxSupplier.${string}`>

export const isRfxSupplierField = (value: RfxTriggerOperand['value']): value is RfxSupplierField =>
  value.startsWith('rfxSupplier.')

/**
 * The rule spelled the way the shared condition editor and validator can read it.
 *
 * Both are built on the request comparison tree, so they only handle its left operands. The RFx
 * operands borrow a VARIABLE under a reserved prefix on the way in and are translated back on the
 * way out, so nothing spelled for their benefit reaches a saved attachment. The prefix is reserved
 * rather than question-shaped, so it cannot collide with a form question name.
 */
const EDITOR_VARIABLE_PREFIX = '__rfx_trigger__:'

export const rfxTriggerEditorVariableId = (value: RfxTriggerOperand['value']): string => `${EDITOR_VARIABLE_PREFIX}${value}`

export const rfxTriggerOperandFromEditorId = (id: string): RfxTriggerOperand | null => {
  if (!id.startsWith(EDITOR_VARIABLE_PREFIX)) return null
  const parsed = RfxTriggerOperandSchema.safeParse({
    type: 'RFX_TRIGGER_VARIABLE',
    value: id.slice(EDITOR_VARIABLE_PREFIX.length),
  })
  return parsed.success ? parsed.data : null
}

// Only a VARIABLE can be an RFx operand in the editor's spelling; every other operand is one the
// request condition reads too, and passes through as it is.
const toRfxTriggerOperand = (operand: LeftOperand | null): RfxTriggerLeftOperand | null =>
  operand?.type === 'VARIABLE' ? (rfxTriggerOperandFromEditorId(operand.value) ?? operand) : operand

const toEditorOperand = (operand: RfxTriggerLeftOperand | null): LeftOperand | null =>
  isRfxTriggerOperand(operand) ? { type: 'VARIABLE', value: rfxTriggerEditorVariableId(operand.value) } : operand

export const toRfxTriggerComparison = (comparison: Comparison): RfxTriggerComparison =>
  mapComparisonLeaves(comparison, (leaf: SingleComparison) => ({ ...leaf, primaryField: toRfxTriggerOperand(leaf.primaryField) }))

export const toEditorComparison = (comparison: RfxTriggerComparison): Comparison =>
  mapComparisonLeaves(comparison, (leaf: RfxTriggerSingleComparison) => ({
    ...leaf,
    primaryField: toEditorOperand(leaf.primaryField),
  }))

const isStageFlag = (value: RfxTriggerOperand['value']): value is RfxStageFlag => value.startsWith('rfx.')
const STAGE_FLAGS = RfxTriggerOperandSchema.shape.value.options.filter(isStageFlag)

// Every RFx has a stage, so the emptiness operators would match every supplier.
const STAGE_FLAG_INVALID_OPERATORS: ConditionOperator[] = ['IS_EMPTY', 'IS_NOT_EMPTY']
// The same exclusions as `supplier.state`, so the RFx supplier's state offers what the request's does.
const STATE_INVALID_OPERATORS: ConditionOperator[] = ['CONTAINS', 'NOT_CONTAINS']

const editorLhs = (value: RfxTriggerOperand['value']) => ({ type: 'VARIABLE' as const, value: rfxTriggerEditorVariableId(value) })

/** The RFx operands as the shared validator and editor see them, in the editor spelling. */
export const rfxTriggerConditionVariables = (): (PrimitiveConditionVariable | StringConditionVariable)[] => [
  ...STAGE_FLAGS.map((flag) =>
    createPrimitiveConditionVariable({
      id: rfxTriggerEditorVariableId(flag),
      lhs: editorLhs(flag),
      resolvedType: 'boolean',
      invalidOperators: STAGE_FLAG_INVALID_OPERATORS,
    })
  ),
  createStringConditionVariable({
    id: rfxTriggerEditorVariableId('rfxSupplier.state'),
    lhs: editorLhs('rfxSupplier.state'),
    discreteOptions: createSupplierStateConditionOptions(),
    invalidOperators: STATE_INVALID_OPERATORS,
  }),
  createStringConditionVariable({
    id: rfxTriggerEditorVariableId('rfxSupplier.isPreferred'),
    lhs: editorLhs('rfxSupplier.isPreferred'),
    discreteOptions: createSupplierPreferredConditionOptions(),
    invalidOperators: SUPPLIER_PREFERRED_INVALID_OPERATORS,
  }),
]
